import { supabase } from './supabase-client';
import { edgeFunctions } from './edge-functions';

export interface WorkExperience {
  id: string;
  user_id: string;
  job_title: string;
  company_name: string;
  start_date: string;
  end_date: string | null;
  description: string;
  is_current: boolean;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface WorkExperienceInput {
  job_title: string;
  company_name: string;
  start_date: string;
  end_date: string | null;
  description?: string;
  is_current?: boolean;
  display_order?: number;
}

export const workExperienceApi = {
  async extractFromResume(resumePath: string): Promise<WorkExperienceInput[]> {
    try {
      const result = await edgeFunctions.extractResumeExperience(resumePath);

      if (!result.success) {
        throw new Error(result.error || 'Failed to extract work experience');
      }

      return result.workExperience || [];
    } catch (error) {
      console.error('Error extracting work experience:', error);
      throw error;
    }
  },

  async getAll(targetUserId?: string): Promise<WorkExperience[]> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('Not authenticated');
    }

    // Use the provided userId or fall back to the current user's ID
    const userId = targetUserId || user.id;

    const { data, error } = await supabase
      .from('work_experience')
      .select('*')
      .eq('user_id', userId)
      .order('display_order', { ascending: true })
      .order('start_date', { ascending: false });

    if (error) {
      console.error('Error fetching work experience:', error);
      throw error;
    }

    return data || [];
  },

  async create(input: WorkExperienceInput): Promise<WorkExperience> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('Not authenticated');
    }

    const { data, error } = await supabase
      .from('work_experience')
      .insert({
        user_id: user.id,
        ...input,
        description: input.description || '',
        is_current: input.is_current || false,
        display_order: input.display_order || 0,
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating work experience:', error);
      throw error;
    }

    return data;
  },

  async bulkCreate(inputs: WorkExperienceInput[]): Promise<WorkExperience[]> {
    const { data: { user } } = await supabase.auth.getUser();

    if (!user) {
      throw new Error('Not authenticated');
    }

    const records = inputs.map((input, index) => ({
      user_id: user.id,
      ...input,
      description: input.description || '',
      is_current: input.is_current || false,
      display_order: input.display_order ?? index,
    }));

    const { data, error } = await supabase
      .from('work_experience')
      .insert(records)
      .select();

    if (error) {
      console.error('Error bulk creating work experience:', error);
      throw error;
    }

    return data || [];
  },

  async update(id: string, updates: Partial<WorkExperienceInput>): Promise<WorkExperience> {
    const { data, error } = await supabase
      .from('work_experience')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) {
      console.error('Error updating work experience:', error);
      throw error;
    }

    return data;
  },

  async delete(id: string): Promise<void> {
    const { error } = await supabase
      .from('work_experience')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting work experience:', error);
      throw error;
    }
  },

  async reorder(orderedIds: string[]): Promise<void> {
    const updates = orderedIds.map((id, index) =>
      supabase
        .from('work_experience')
        .update({ display_order: index })
        .eq('id', id)
    );

    await Promise.all(updates);
  },
};
